<template>
  <div class="file-viewer-component">
    <div class="box">
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <h4 class="title is-4">{{ $t("archive.file_viewer") }}</h4>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <div class="field has-addons">
              <div class="control">
                <div class="select is-small">
                  <select v-model="selectedCustomer" @change="onCustomerChange">
                    <option value="">
                      {{ $t("archive.select_customer") }}
                    </option>
                    <option
                      v-for="customer in customers"
                      :key="customer.id"
                      :value="customer.id"
                    >
                      {{ customer.name }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="control">
                <div class="select is-small">
                  <select
                    v-model="selectedExpedient"
                    @change="onExpedientChange"
                    :disabled="!selectedCustomer"
                  >
                    <option value="">
                      {{ $t("archive.select_expedient") }}
                    </option>
                    <option
                      v-for="expedient in filteredExpedients"
                      :key="expedient.id"
                      :value="expedient.id"
                    >
                      {{ expedient.name }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="control">
                <button
                  class="button is-small is-info"
                  @click="refreshFiles"
                  :class="{ 'is-loading': loadingFiles }"
                  :disabled="!selectedExpedient"
                >
                  <span class="icon">
                    <i class="fas fa-sync-alt"></i>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- File List and Viewer Layout -->
      <div class="columns" v-if="selectedExpedient">
        <!-- File List Sidebar -->
        <div class="column is-3">
          <div class="file-list-panel">
            <h5 class="title is-5">{{ $t("archive.files") }}</h5>

            <div v-if="loadingFiles" class="has-text-centered">
              <div class="loader"></div>
              <p class="is-size-7">{{ $t("archive.loading_files") }}</p>
            </div>

            <div v-else-if="files.length === 0" class="has-text-centered">
              <div class="content">
                <p class="has-text-grey">
                  <i class="fas fa-file fa-2x"></i>
                </p>
                <p class="is-size-7">
                  {{ $t("archive.no_files_in_expedient") }}
                </p>
              </div>
            </div>

            <div v-else class="file-list">
              <div
                v-for="file in files"
                :key="file.id"
                class="file-item"
                :class="{
                  'is-active': selectedFile && selectedFile.id === file.id,
                }"
                @click="selectFile(file)"
              >
                <div class="file-item-content">
                  <div class="file-icon">
                    <i :class="getFileIcon(file.file_type)"></i>
                  </div>
                  <div class="file-info">
                    <p class="file-name" :title="file.original_filename">
                      {{ truncateFilename(file.original_filename) }}
                    </p>
                    <p class="file-meta">
                      <span
                        class="tag is-small"
                        :class="getFileTypeClass(file.file_type)"
                      >
                        {{ file.file_type.toUpperCase() }}
                      </span>
                      <span class="file-size">{{
                        formatFileSize(file.file_size)
                      }}</span>
                    </p>
                  </div>
                  <div class="file-actions">
                    <button
                      class="button is-small is-info"
                      @click.stop="downloadFile(file)"
                      :title="$t('archive.download')"
                    >
                      <span class="icon">
                        <i class="fas fa-download"></i>
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- File Viewer Panel -->
        <div class="column is-9">
          <div class="file-viewer-panel">
            <div v-if="!selectedFile" class="empty-viewer">
              <div class="content has-text-centered">
                <p class="title is-3 has-text-grey-light">
                  <i class="fas fa-file-alt"></i>
                </p>
                <p class="subtitle has-text-grey">
                  {{ $t("archive.select_file_to_view") }}
                </p>
              </div>
            </div>

            <div v-else class="file-display">
              <!-- File Header -->
              <div class="file-header">
                <div class="level">
                  <div class="level-left">
                    <div class="level-item">
                      <span class="icon-text">
                        <span class="icon is-large">
                          <i
                            :class="getFileIcon(selectedFile.file_type)"
                            style="font-size: 1.5rem"
                          ></i>
                        </span>
                        <div>
                          <h5 class="title is-5">
                            {{ selectedFile.original_filename }}
                          </h5>
                          <p class="subtitle is-6">
                            {{ formatFileSize(selectedFile.file_size) }} •
                            {{ selectedFile.file_type.toUpperCase() }} •
                            {{ formatDate(selectedFile.create_date) }}
                          </p>
                        </div>
                      </span>
                    </div>
                  </div>
                  <div class="level-right">
                    <div class="level-item">
                      <div class="buttons">
                        <button
                          class="button is-info"
                          @click="downloadFile(selectedFile)"
                        >
                          <span class="icon">
                            <i class="fas fa-download"></i>
                          </span>
                          <span>{{ $t("archive.download") }}</span>
                        </button>
                        <button
                          class="button is-primary"
                          @click="openFullscreen"
                          v-if="canPreview(selectedFile)"
                        >
                          <span class="icon">
                            <i class="fas fa-expand"></i>
                          </span>
                          <span>{{ $t("archive.fullscreen") }}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- File Content -->
              <div class="file-content">
                <!-- PDF Viewer -->
                <div v-if="selectedFile.file_type === 'pdf'" class="pdf-viewer">
                  <iframe
                    :src="getFileUrl(selectedFile)"
                    width="100%"
                    height="600px"
                    style="border: none; border-radius: 6px"
                  >
                  </iframe>
                </div>

                <!-- Image Viewer -->
                <div
                  v-else-if="isImage(selectedFile)"
                  class="image-viewer has-text-centered"
                >
                  <figure class="image">
                    <img
                      :src="getFileUrl(selectedFile)"
                      :alt="selectedFile.original_filename"
                      style="
                        max-width: 100%;
                        max-height: 600px;
                        object-fit: contain;
                        border-radius: 6px;
                      "
                    />
                  </figure>
                </div>

                <!-- Text Viewer -->
                <div
                  v-else-if="selectedFile.file_type === 'txt'"
                  class="text-viewer"
                >
                  <div class="box" style="height: 600px; overflow-y: auto">
                    <div v-if="textContent">
                      <pre>{{ textContent }}</pre>
                    </div>
                    <div
                      v-else
                      class="has-text-centered"
                      style="padding-top: 200px"
                    >
                      <button
                        class="button is-primary"
                        @click="loadTextContent"
                      >
                        <span class="icon">
                          <i class="fas fa-file-alt"></i>
                        </span>
                        <span>{{ $t("archive.load_text_content") }}</span>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Unsupported File Types -->
                <div v-else class="unsupported-viewer">
                  <div
                    class="box"
                    style="
                      height: 600px;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                    "
                  >
                    <div class="content has-text-centered">
                      <p class="title is-2 has-text-grey-light">
                        <i
                          :class="getFileIcon(selectedFile.file_type)"
                          style="font-size: 4rem"
                        ></i>
                      </p>
                      <p class="title is-4">
                        {{ selectedFile.original_filename }}
                      </p>
                      <p class="subtitle">
                        {{ getPreviewMessage(selectedFile) }}
                      </p>
                      <button
                        class="button is-primary is-large"
                        @click="downloadFile(selectedFile)"
                      >
                        <span class="icon">
                          <i class="fas fa-download"></i>
                        </span>
                        <span>{{ $t("archive.download_to_view") }}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- File Metadata -->
              <div
                class="file-metadata"
                v-if="selectedFile.description || selectedFile.tags"
              >
                <div class="box">
                  <h6 class="title is-6">{{ $t("archive.metadata") }}</h6>

                  <div v-if="selectedFile.description" class="field">
                    <label class="label">{{ $t("archive.description") }}</label>
                    <div class="content">
                      <p>{{ selectedFile.description }}</p>
                    </div>
                  </div>

                  <div v-if="selectedFile.tags" class="field">
                    <label class="label">{{ $t("archive.tags") }}</label>
                    <div class="tags">
                      <span
                        v-for="tag in parseTags(selectedFile.tags)"
                        :key="tag"
                        class="tag is-light"
                      >
                        {{ tag }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Selection State -->
      <div v-else class="no-selection has-text-centered">
        <div class="content">
          <p class="title is-3 has-text-grey-light">
            <i class="fas fa-folder-open"></i>
          </p>
          <p class="subtitle has-text-grey">
            {{ $t("archive.select_expedient_to_view_files") }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "FileViewer",
  props: {
    initialCustomerId: {
      type: [String, Number],
      default: null,
    },
    initialExpedientId: {
      type: [String, Number],
      default: null,
    },
    initialFileId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      customers: [],
      expedients: [],
      files: [],
      selectedCustomer: "",
      selectedExpedient: "",
      selectedFile: null,
      textContent: "",
      loadingFiles: false,
    };
  },
  computed: {
    filteredExpedients() {
      if (!this.selectedCustomer) return [];
      return this.expedients.filter(
        (exp) => exp.customer_id === this.selectedCustomer
      );
    },
  },
  mounted() {
    this.loadInitialData();
  },
  methods: {
    async loadInitialData() {
      try {
        // Load customers
        const customersResponse = await this.$api.get(
          "/api/v1/archive/customer/catalog"
        );
        this.customers = customersResponse.data;

        // Load expedients
        const expedientsResponse = await this.$api.get(
          "/api/v1/archive/expedient/catalog"
        );
        this.expedients = expedientsResponse.data;

        // Set initial selections if provided
        if (this.initialCustomerId) {
          this.selectedCustomer = this.initialCustomerId;
          if (this.initialExpedientId) {
            this.selectedExpedient = this.initialExpedientId;
            await this.loadFiles();

            if (this.initialFileId) {
              const file = this.files.find((f) => f.id === this.initialFileId);
              if (file) {
                this.selectFile(file);
              }
            }
          }
        }
      } catch (error) {
        console.error("Error loading initial data:", error);
        this.$toast.error(this.$t("archive.error_loading_data"));
      }
    },

    onCustomerChange() {
      this.selectedExpedient = "";
      this.files = [];
      this.selectedFile = null;
      this.textContent = "";
    },

    onExpedientChange() {
      this.files = [];
      this.selectedFile = null;
      this.textContent = "";
      if (this.selectedExpedient) {
        this.loadFiles();
      }
    },

    async loadFiles() {
      if (!this.selectedExpedient) return;

      this.loadingFiles = true;
      try {
        const response = await this.$api.get("/api/v1/archive/file", {
          params: { expedient_id: this.selectedExpedient },
        });
        this.files = response.data.data || [];
      } catch (error) {
        console.error("Error loading files:", error);
        this.$toast.error(this.$t("archive.error_loading_files"));
      } finally {
        this.loadingFiles = false;
      }
    },

    refreshFiles() {
      this.loadFiles();
    },

    selectFile(file) {
      this.selectedFile = file;
      this.textContent = "";

      // Emit event for parent components
      this.$emit("file-selected", file);
    },

    async loadTextContent() {
      if (!this.selectedFile || this.selectedFile.file_type !== "txt") return;

      try {
        const response = await this.$api.get(
          `/api/v1/archive/file/${this.selectedFile.id}/download`,
          {
            responseType: "text",
          }
        );
        this.textContent = response.data;
      } catch (error) {
        console.error("Error loading text content:", error);
        this.$toast.error(this.$t("archive.error_loading_text"));
      }
    },

    async downloadFile(file) {
      try {
        const response = await this.$api.get(
          `/api/v1/archive/file/${file.id}/download`,
          {
            responseType: "blob",
          }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", file.original_filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error downloading file:", error);
        this.$toast.error(this.$t("archive.error_downloading_file"));
      }
    },

    openFullscreen() {
      if (this.selectedFile && this.canPreview(this.selectedFile)) {
        window.open(this.getFileUrl(this.selectedFile), "_blank");
      }
    },

    getFileUrl(file) {
      return `/api/v1/archive/file/${file.id}/download`;
    },

    canPreview(file) {
      return file.file_type === "pdf" || this.isImage(file);
    },

    isImage(file) {
      return ["jpg", "jpeg", "png", "gif"].includes(
        file.file_type.toLowerCase()
      );
    },

    getPreviewMessage(file) {
      if (this.isImage(file)) {
        return this.$t("archive.image_preview_available");
      } else if (file.file_type === "pdf") {
        return this.$t("archive.pdf_preview_available");
      } else if (file.file_type === "txt") {
        return this.$t("archive.text_preview_available");
      } else if (
        ["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(
          file.file_type.toLowerCase()
        )
      ) {
        return this.$t("archive.office_preview_not_available");
      } else {
        return this.$t("archive.preview_not_supported");
      }
    },

    getFileIcon(fileType) {
      const iconMap = {
        pdf: "fas fa-file-pdf has-text-danger",
        doc: "fas fa-file-word has-text-info",
        docx: "fas fa-file-word has-text-info",
        xls: "fas fa-file-excel has-text-success",
        xlsx: "fas fa-file-excel has-text-success",
        ppt: "fas fa-file-powerpoint has-text-warning",
        pptx: "fas fa-file-powerpoint has-text-warning",
        jpg: "fas fa-file-image has-text-primary",
        jpeg: "fas fa-file-image has-text-primary",
        png: "fas fa-file-image has-text-primary",
        gif: "fas fa-file-image has-text-primary",
        txt: "fas fa-file-alt has-text-grey",
        zip: "fas fa-file-archive has-text-dark",
        rar: "fas fa-file-archive has-text-dark",
      };

      return iconMap[fileType.toLowerCase()] || "fas fa-file has-text-grey";
    },

    getFileTypeClass(fileType) {
      const classMap = {
        pdf: "is-danger",
        doc: "is-info",
        docx: "is-info",
        xls: "is-success",
        xlsx: "is-success",
        ppt: "is-warning",
        pptx: "is-warning",
        jpg: "is-primary",
        jpeg: "is-primary",
        png: "is-primary",
        gif: "is-primary",
        txt: "is-light",
        zip: "is-dark",
        rar: "is-dark",
      };

      return classMap[fileType.toLowerCase()] || "is-light";
    },

    truncateFilename(filename, maxLength = 25) {
      if (filename.length <= maxLength) return filename;
      const extension = filename.split(".").pop();
      const name = filename.substring(0, filename.lastIndexOf("."));
      const truncatedName =
        name.substring(0, maxLength - extension.length - 4) + "...";
      return truncatedName + "." + extension;
    },

    parseTags(tagsString) {
      if (!tagsString) return [];
      return tagsString
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);
    },

    formatDate(dateString) {
      if (!dateString) return "";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString() + " " + date.toLocaleTimeString();
      } catch (error) {
        return dateString;
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return "0 B";

      const k = 1024;
      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },
  },
};
</script>

<style scoped>
.file-viewer-component {
  padding: 20px;
}

.file-list-panel {
  height: 100%;
  border-right: 1px solid #dbdbdb;
  padding-right: 15px;
}

.file-list {
  max-height: 600px;
  overflow-y: auto;
}

.file-item {
  border: 1px solid #dbdbdb;
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.file-item:hover {
  border-color: #3273dc;
  box-shadow: 0 2px 8px rgba(50, 115, 220, 0.1);
}

.file-item.is-active {
  border-color: #3273dc;
  background-color: #e8f4fd;
}

.file-item-content {
  display: flex;
  align-items: center;
  padding: 10px;
}

.file-icon {
  width: 30px;
  text-align: center;
  margin-right: 10px;
}

.file-info {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-weight: 500;
  margin-bottom: 4px;
  word-wrap: break-word;
}

.file-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.file-size {
  font-size: 0.75rem;
  color: #666;
}

.file-actions {
  opacity: 0;
  transition: opacity 0.2s ease;
}

.file-item:hover .file-actions {
  opacity: 1;
}

.file-viewer-panel {
  padding-left: 15px;
}

.file-header {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #dbdbdb;
}

.file-content {
  margin-bottom: 20px;
}

.icon-text {
  align-items: center;
  display: flex;
}

.icon-text .icon {
  margin-right: 0.75rem;
}

.pdf-viewer iframe,
.image-viewer img {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.text-viewer pre {
  font-family: "Courier New", monospace;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: 0.875rem;
  line-height: 1.4;
}

.empty-viewer,
.no-selection {
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loader {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3273dc;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;
  margin: 0 auto 10px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
